import React, { Component } from "react";
import { Body, Title } from "@vds/typography";
import * as Actions from "../../store/actions/global/global";
import { withRouter } from "../../withRouter";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { changeLanguage } from "../../util/userLanguageUtils";
import { Wrapper, CPButton, CustomisedTextLink, CustomisedTitle, TextWrapper } from "../../constants/common/StyledGlobalConstants";
import styled from "styled-components";
import * as BillingActions from "../../store/actions/billing/billing";
import Cookies from "js-cookie";
import { findRoutingPath } from "../../util/helpURLNavigation";
import { isNonERPCountryAccount } from "../../util/nonERPCountryUtils";
import { getDefaultLandingPermission } from "../../util/permissionUtils";
import RedIcon from "../../assets/icons/Red-icon.svg";
import { ParentFlexContainer } from "../../components/billingPayment/SuspendedAccNotification";
import { getCountryContact } from "../../components/common/CountryCode";
import { REDBALL_FEATURE_TOGGLE, suspendedFinancialStatuses } from "../../constants/appConstants";
import { Colors } from "../../constants/Colors";
import { SEAMLESS_ACCESS_CONSTANTS, COMMON_CONSTANTS } from "../../constants/StringConstants";
import EmailLink from "../../components/common/EmailLink";
import { pendoTrack } from "../../util/pendoUtil";
import PropTypes from "prop-types";
import MultiContractDropDown from "../../components/common/MultiContractDropDown";

// Define constants for widths
const MOBILE_WIDTH = "100%";
const DESKTOP_WIDTH = "32rem";

// Helper function to determine width
const getWidth = (windowSize) => (windowSize.width <= 480 ? MOBILE_WIDTH : DESKTOP_WIDTH);
const getContentWidth = (windowSize) => (windowSize.width <= 480 ? "100%" : "80%");
const getButtonWidth = (windowSize) => (windowSize.width > 480 ? "80%" : "100%");

const focusDelay = 300; // Delay in milliseconds
const validFinancialStatuses = ["Active", "Reactivated"];
const DropdownValues = styled.div`
  display: flex;
  align-items: center;
`;

const getBackgroundColor = (state) => {
  if (state.isDisabled) return Colors.whiteSmoke;
  if (state.isSelected && state.isFocused) return Colors.activeDefault;
  if (state.isFocused) return Colors.activeDefault;
  return Colors.white;
};

const getColor = (state) => {
  if (state.isDisabled) return Colors.dimGray;
  if (state.isSelected && state.isFocused) return Colors.white;
  if (state.isFocused) return Colors.white;
  return Colors.black;
};

const dropDownStyle = {
  indicatorSeparator: () => null,
  dropdownIndicator: (base) => ({
    ...base,
    color: Colors.black,
  }),

  option: (provided, state) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    backgroundColor: getBackgroundColor(state),
    color: getColor(state),
    paddingLeft: "17px",
    fontSize: "0.875rem !important",
    fontWeight: "400",
    height: "48px",
    cursor: state.isDisabled ? "normal" : "pointer",
    fontFamily: "neue-haas-grotesk-text",
    "&:hover": {
      backgroundColor: !state.isDisabled && Colors.activeDefault,
      color: !state.isDisabled && Colors.white,
    },
  }),
  control: (base, state) => ({
    ...base,
    color: Colors.black,
    fontFamily: "neue-haas-grotesk-text",
    fontSize: "0.875rem",
    fontWeight: "400",
    height: "44px",
    paddingLeft: "5px",
    borderColor: state.isFocused ? Colors.black : Colors.grayBorder, // Black when focused, light grey when normal
    boxShadow: state.isFocused ? Colors.black : "none", // Black shadow when focused, none when normal
    cursor: "pointer",
    "&:hover": {
      borderColor: Colors.black, // Black when hovered
    },
  }),
  placeholder: (provided, state) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    // paddingLeft: '6px',
    color: Colors.black,
  }),
};
class SeamlessAccess extends Component {
  constructor(props) {
    super(props);
    this.suspendedNotificationRef = React.createRef();
    this.suspendedNotificationTimeout = null;
    this.currentRef = React.createRef();
    this.state = {
      selectedContractNumber: "",
      contractNumbersWithoutPayment: [],
      isSuspendedNotificationCalled: false,
      isSelectedContractNumberSuspended: false,
    };
  }

  componentDidMount() {
    this.focusElement();
    let language = this.props.userLanguage.replace(/-/g, "");
    this.setState({ userLanguage: language }, () => {
      let nameSpace = changeLanguage(language, "login");
      this.setState({ nameSpace: nameSpace }, () => {
        this.props.i18n.changeLanguage(language);
      });
    });

    // Populate contractNumbersWithoutPayment if data is already available
    if (Object.keys(this.props.paymentStatus).length && Object.keys(this.props.erpBusinessPartner).length) {
      this.updateContractNumbersWithoutPayment();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.paymentStatus !== this.props.paymentStatus) {
      this.updateContractNumbersWithoutPayment();
    }
    if (!this.state.isSuspendedNotificationCalled) {
      if (this.isSuspendedNotification()) {
        pendoTrack("Customer Portal | Seamless Access | Multiple Contract page | Suspended Notification", {
          status: "Multiple Contract page - Suspended notification displayed",
          UniversalAccountId: this.props.UniversalAccountId,
        });
        this.setState({ isSuspendedNotificationCalled: true });
        this.focusSuspendedNotification();
      }
    }
  }
  componentWillUnmount() {
    // To clear the timeout to prevent memory leaks
    if (this.suspendedNotificationTimeout) {
      clearTimeout(this.suspendedNotificationTimeout);
    }
  }

  updateContractNumbersWithoutPayment = () => {
    const { paymentStatus, erpBusinessPartner } = this.props;

    // Map contractAccountId to erpCANumber and financialStatus
    const contractAccountMap = erpBusinessPartner.data.contractAccountDetails.reduce((map, item) => {
      map[item.contractAccountId] = {
        erpCANumber: item.erpCANumber,
        financialStatus: item.financialStatus,
      };
      return map;
    }, {});

    // Filter suspended accounts
    const suspendedAccounts = paymentStatus?.data?.filter((item) => {
      const accountDetails = contractAccountMap[item.contractAccountId];
      return suspendedFinancialStatuses.includes(accountDetails?.financialStatus?.toLowerCase());
    });

    // Check if all suspended accounts have paymentStatus as "true"
    const allSuspendedPaymentsTrue = suspendedAccounts?.every((item) => item.paymentStatus === "true");

    // Filter active accounts
    // const activeAccounts = paymentStatus?.data?.filter((item) => {
    //   const accountDetails = contractAccountMap[item.contractAccountId];
    //   return validFinancialStatuses.includes(accountDetails?.financialStatus);
    // });

    // Determine isDisabled for each contract account
    const filteredCANumbers = paymentStatus?.data?.map((item) => {
      const accountDetails = contractAccountMap[item.contractAccountId];
      const isSuspended = suspendedFinancialStatuses.includes(accountDetails?.financialStatus?.toLowerCase());
      const isActive = validFinancialStatuses.includes(accountDetails?.financialStatus);

      let isDisabled = false;

      if (isSuspended) {
        // For suspended accounts
        if (!allSuspendedPaymentsTrue && item?.paymentStatus === "true") {
          isDisabled = true; // Disable if not all suspended accounts have paymentStatus as "true"
        }
      } else if (isActive) {
        // For active accounts
        if (!allSuspendedPaymentsTrue) {
          isDisabled = true; // Disable active accounts if not all suspended accounts have paymentStatus as "true"
        }
      }

      return {
        contractAccountId: item?.contractAccountId,
        paymentStatus: item?.paymentStatus,
        erpCANumber: accountDetails?.erpCANumber,
        financialStatus: accountDetails?.financialStatus,
        isDisabled,
      };
    });

    // Update state with the filtered data
    this.setState({ contractNumbersWithoutPayment: filteredCANumbers });
  };

  isSuspendedNotification = () => {
    return REDBALL_FEATURE_TOGGLE &&
      suspendedFinancialStatuses.includes(this.props.erpBusinessPartner.data.accountFinancialStatus?.toLowerCase()) &&
      this.state.contractNumbersWithoutPayment?.some(
        (item) => suspendedFinancialStatuses.includes(item.financialStatus?.toLowerCase()) && item.paymentStatus !== "true"
      );
  };

  /* Added function to add a focus to first element as soon as page load for accessbility issue fix*/
  focusElement = () => {
     this.currentRef?.current?.focus();
    window.scrollTo(0, 0); // Scroll to top of the page talkback
  };
  /* Added function to add a focus to  the suspended notification div to fix talkback issue*/
  focusSuspendedNotification = () => {
    if (this.suspendedNotificationRef?.current) {
      this.suspendedNotificationTimeout = setTimeout(() => {
        this.suspendedNotificationRef.current.focus(); // Programmatically focus the element
      }, focusDelay); // Delay of 300ms to ensure the element is rendered before focusing
    }
  };

  handleChange = (item) => {
    this.setState({ 
      selectedContractNumber: item?.value , 
      isSelectedContractNumberSuspended:item?.isAccountSuspended
       });
  };

  handleSubmit = (e) => {
    e.preventDefault();
    window.newrelic?.addPageAction("MultipleContract", { value: this.state.selectedContractNumber });

    const selectedCA = this.getSelectedContractAccount();
    this.props.setContractAccount(this.state.selectedContractNumber);

    if (selectedCA.length > 0) {
      this.handleContractNumbersSuccess();
      const isNonERPCountryAcc = this.isNonERPCountryAccount();
      const permissionName = this.getPermissionName(isNonERPCountryAcc);
      this.props.landOnAuthorizedPage(permissionName);
    }
  };

  getSelectedContractAccount = () => {
    return this.props.erpBusinessPartner.data.contractAccountDetails.filter((item) => this.state.selectedContractNumber === item.erpCANumber);
  };

  handleContractNumbersSuccess = () => {
    this.props.getContractNumbersSuccess({
      accountID: this.props.erpBusinessPartner.data.accountId,
      businessPartnerNumber: this.props.erpBusinessPartner.data.businessPartnerNumber,
      vztSegment: this.props.erpBusinessPartner.data.vztSegment,
      accountFinancialStatus: this.props.accountFinancialStatus,
      brands: this.props.erpBusinessPartner.data.brands || "",
      data: this.props.erpBusinessPartner,
    });
  };

  isNonERPCountryAccount = () => {
    return this.props.contractAccountCountry ? isNonERPCountryAccount(this.props.contractAccountCountry) : false;
  };

  getPermissionName = (isNonERPCountryAcc) => {
    if (this.props.userType && this.props.userType !== "2" && this.props.userType !== "4") {
      const fullAndReadOnlyPermissions = this.getFullAndReadOnlyPermissions();
      return getDefaultLandingPermission(fullAndReadOnlyPermissions, isNonERPCountryAcc);
    } else {
      return findRoutingPath(
        Cookies.get("currentRoute"),
        isNonERPCountryAcc
        // , this.props.regionId
      );
    }
  };

  getFullAndReadOnlyPermissions = () => {
    const permissions = {
      viewInvoicesPermission: this.props.viewInvoicesPermission,
      managePaymentsPermission: this.props.managePaymentsPermission,
      accountProfilePermission: this.props.accountProfilePermission,
      viewallotherCasesPermission: this.props.viewallotherCasesPermission,
      viewFinancialCasesPermission: this.props.viewFinancialCasesPermission,
      manageyourcontractandservicesPermission: this.props.manageyourcontractandservicesPermission,
      viewtechnicianAppointmentsPermission: "Full", // Default value
    };

    // Filter out undefined permissions
    return Object.fromEntries(Object.entries(permissions).filter(([_, value]) => value));
  };

  hasContractAccountDetails = () => {
    return (
      this.props?.erpBusinessPartner?.data?.contractAccountDetails?.length
    );
  };

  render() {
    const isNA = this.props.accountUserTokenDetails?.isNA;
    return (
        <div
          style={{
            outline: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: `${this.props.windowSize.width <= 480 ? "0 1rem 0 1rem" : ""}`,
          }}
          tabIndex={-1}
          ref={this.currentRef}
        >
          <div
            className={this.props.windowSize.width <= 679 && "MainContent"}
            style={{
              display: "flex",
              height: "100vh",
              alignItems: "center",
              flexDirection: "column",
              paddingTop: `${
                REDBALL_FEATURE_TOGGLE &&
                suspendedFinancialStatuses.includes(this.props.erpBusinessPartner.data.accountFinancialStatus?.toLowerCase()) &&
                this.state.contractNumbersWithoutPayment?.some(
                  (item) => suspendedFinancialStatuses.includes(item.financialStatus?.toLowerCase()) && item.paymentStatus !== "true"
                )
                  ? "7rem"
                  : "30vh"
              }`,
              width: getWidth(this.props.windowSize),
            }}
          >
            <div style={{ width: getContentWidth(this.props.windowSize) }}>
              <CustomisedTitle
                fontSize={
                  this.props.windowSize.width <= 480 && (this.props.userLanguage === "nl-NL" || this.props.userLanguage === "de-DE")
                    ? "2.125rem"
                    : "2.5rem"
                }
                lineHeight="3rem"
              >
                <h1 className="sr-only">
                  {this.props.t(SEAMLESS_ACCESS_CONSTANTS.SELECT_CONTRACT, {
                    ns: `${this.state.nameSpace}`,
                  })}
                </h1>
                <Title size="large" tabIndex="-1" aria-hidden="true">
                  {this.props.t(SEAMLESS_ACCESS_CONSTANTS.SELECT_CONTRACT, {
                    ns: `${this.state.nameSpace}`,
                  })}
                </Title>
              </CustomisedTitle>
            </div>

            {this.isSuspendedNotification() && (
              <div
                style={{
                  width: `${this.props.windowSize.width > 480 ? "80%" : "100%"}`,
                }}
              >
                <ParentFlexContainer background="#FFECE0" marginTop="1rem">
                  <div
                    role="alert"
                    aria-live="assertive"
                    id="multi-account-suspended-notification"
                    className="verification-suspended-info"
                    style={{ margin: "10px" }}
                    ref={this.suspendedNotificationRef}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <img
                        aria-hidden="true"
                        aria-label="Red Icon"
                        alt="red-icon"
                        src={RedIcon}
                        style={{
                          width: "14px",
                          height: "20px",
                          marginRight: "8px", // Add some space between the image and the text
                        }}
                      />
                      <TextWrapper fontWeight="700 !important">
                        {this.props.t(COMMON_CONSTANTS.ACCOUNT_SUSPENDED, { ns: `${this.state.nameSpace}` })}
                      </TextWrapper>
                    </div>
                    <div style={{ marginTop: "10px", marginLeft: "21.5px" }}>
                      <Body size="small">{this.props.t(SEAMLESS_ACCESS_CONSTANTS.SUSPENDED_CA_TEXT, { ns: `${this.state.nameSpace}` })}</Body>
                      <ul style={{ paddingLeft: "25px", marginTop: "5px" }}>
                        {this.hasContractAccountDetails() &&
                          this.state.contractNumbersWithoutPayment
                            ?.filter(
                              (item) => suspendedFinancialStatuses.includes(item.financialStatus?.toLowerCase()) && item.paymentStatus !== "true"
                            )
                            ?.map((item) => (
                              <li key={item.erpCANumber}>
                                <Body size="small">{item.erpCANumber}</Body>
                              </li>
                            ))}
                      </ul>
                    </div>
                    <div style={{ marginTop: "10px", marginLeft: "21.5px", wordBreak: "break-word", overflowWrap: "break-word" }}>
                      {isNA ? (
                        <Body size="small">
                          {this.props.t(SEAMLESS_ACCESS_CONSTANTS.SUSPENDED_TEXT, { ns: `${this.state.nameSpace}` })}
                          {this.props.windowSize.width > 400 && <br />}
                          {" " + this.props.t(getCountryContact(this.props.regionId)?.phone, { ns: `${this.state.nameSpace}` }) + "."}
                        </Body>
                      ) : (
                        <>
                          <Body size="small">{this.props.t(SEAMLESS_ACCESS_CONSTANTS.SUSPENDED_TEXT, { ns: `${this.state.nameSpace}` })}:</Body>
                          <ul style={{ paddingLeft: "25px", marginTop: "20px" }}>
                            <li key={COMMON_CONSTANTS.EMAIL}>
                              <Body size="small">
                                {this.props.t(COMMON_CONSTANTS.EMAIL, { ns: `${this.state.nameSpace}` })}:&nbsp;
                                <EmailLink
                                  email={this.props.t(getCountryContact(this.props.regionId)?.email)}
                                  color={Colors.frenchBlue}
                                  fontWeight={400}
                                />
                              </Body>
                            </li>
                            <li key={COMMON_CONSTANTS.PHONE}>
                              <Body size="small">
                                {this.props.t(COMMON_CONSTANTS.PHONE, { ns: `${this.state.nameSpace}` })}:{" "}
                                {this.props.t(getCountryContact(this.props.regionId)?.phone, { ns: "redBallTranslation" })}
                              </Body>
                            </li>
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                </ParentFlexContainer>
              </div>
            )}

            <div
              style={{
                width: `${this.props.windowSize.width > 480 ? "80%" : "100%"}`,
                marginTop: "1rem",
              }}
            >
              <div id="verificationInfo" className="verification-desc">
                <Body size="small">{this.props.t(SEAMLESS_ACCESS_CONSTANTS.CHOOSE_CA_TEXT, { ns: `${this.state.nameSpace}` })}</Body>
              </div>
              <Wrapper>
                <Wrapper>
                  {/* <RedesignedDropdown
                          id="inputContainerID"
                          label={this.props.t("Contract Account Number", {
                            ns: `${this.state.nameSpace}`,
                          })}
                          isRequired={false}
                          ariaLabel="Contract Account Number Dropdown"
                          handleOnSelect={(value) => this.handleChange(value)}
                          value={
                            this.state.selectedContractNumber
                              ? this.state.selectedContractNumber
                              : this.props.t("Choose your Contract Account Number", { ns: `${this.state.nameSpace}` })
                          }
                          optionList={
                            this.props.erpBusinessPartner.data &&
                            this.props.erpBusinessPartner.data.contractAccountDetails &&
                            this.props.erpBusinessPartner.data.contractAccountDetails.length &&
                            this.props.erpBusinessPartner.data.contractAccountDetails
                              .filter((item) => validFinancialStatuses.includes(item.financialStatus))
                              .map((item) => item.erpCANumber)
                          }
                          userLanguage={this.props.userLanguage}
                          isUserVerification={true}
                          windowSize={this.props.windowSize}
                          firstOption={this.props.t("Choose your Contract Account Number", { ns: `${this.state.nameSpace}` })}
                          helperText={
                            <>
                              <Body id="userVerification-helper-text" size="small">
                                {this.props.t("You can find your Contract Account Number on the first page of a recent invoice.", {
                                  ns: `${this.state.nameSpace}`,
                                })}{" "}
                                <CustomisedTextLink
                                  id="show-me-link"
                                  color="#747676"
                                  onClick={this.props.helpFindCAN}
                                  size="small"
                                  type="standAlone"
                                  tabIndex="0"
                                  role="link"
                                  ariaLabel="click to view an Invoice sample "
                                >
                                  {this.props.t("Show me", {
                                    ns: `${this.state.nameSpace}`,
                                  })}
                                </CustomisedTextLink>
                              </Body>
                            </>
                          }
                        ></RedesignedDropdown> */}

                  <TextWrapper marginTop="24px !important" marginBottom="8px" fontWeight="700" fontSize="0.75rem">
                    {this.props.t(SEAMLESS_ACCESS_CONSTANTS.CA_DROPDOWN_LABEL, { ns: `${this.state.nameSpace}` })}
                  </TextWrapper>

                  {/* <Select
                    id="multi-contract-account-dropdown"
                    isSearchable={false}
                    tabIndex={0}
                    aria-label="Contract Account Number Dropdown"
                    onChange={(item) => this.handleChange(item?.value)}
                    placeholder={this.props.t(SEAMLESS_ACCESS_CONSTANTS.CA_DROPDOWN_PLACEHOLDER, { ns: `${this.state.nameSpace}` })}
                    // menuIsOpen={true}
                    maxMenuHeight={200}
                    components={{ Option: CustomOption }}
                    menuPlacement={getMenuPlacement(this.props.windowSize)}
                    styles={dropDownStyle}
                    options={
                      this.hasContractAccountDetails() &&
                      (REDBALL_FEATURE_TOGGLE &&
                      suspendedFinancialStatuses.includes(this.props.erpBusinessPartner.data.accountFinancialStatus?.toLowerCase())
                        ? this.state.contractNumbersWithoutPayment?.map((item) => ({
                            value: item.erpCANumber,
                            label:
                              suspendedFinancialStatuses.includes(item.financialStatus?.toLowerCase()) && item.paymentStatus !== "true" ? (
                                <DropdownValues>
                                  {item.erpCANumber}
                                  <img
                                    aria-hidden="true"
                                    role="img"
                                    src={RedIcon}
                                    aria-label="Red Icon"
                                    alt="red-icon"
                                    style={{ width: "14px", height: "20px", marginLeft: "8px" }}
                                  />
                                </DropdownValues>
                              ) : (
                                item.erpCANumber
                              ),
                            isDisabled: item.isDisabled,
                          }))
                        : this.props.erpBusinessPartner.data.contractAccountDetails.map((item) => ({
                            value: item.erpCANumber,
                            label: item.erpCANumber,
                            isDisabled: !validFinancialStatuses.includes(item.financialStatus),
                          })))
                    }
                    // Commenting this for now to handle other language changes
                    // userLanguage={this.props.userLanguage}
                    // isUserVerification={true}
                    // windowSize={this.props.windowSize}
                  /> */}

                    <MultiContractDropDown
                                  options={
                                    this.hasContractAccountDetails() &&
                                    (
                                      REDBALL_FEATURE_TOGGLE && suspendedFinancialStatuses.includes(this.props.erpBusinessPartner.data.accountFinancialStatus?.toLowerCase())
                                        ? this.state.contractNumbersWithoutPayment?.map((item) => ({
                                            value: item.erpCANumber,
                                            label: item.erpCANumber,
                                            isDisabled: item.isDisabled,
                                            isAccountSuspended: suspendedFinancialStatuses.includes(item.financialStatus?.toLowerCase()) && item.paymentStatus !== "true"
                                          }))
                                        : this.props.erpBusinessPartner.data.contractAccountDetails.map((item) => ({
                                            value: item.erpCANumber,
                                            label: item.erpCANumber,
                                            isDisabled: !validFinancialStatuses.includes(item.financialStatus),
                                            isAccountSuspended: suspendedFinancialStatuses.includes(item.financialStatus?.toLowerCase()) && item.paymentStatus !== "true"
                                          }))
                                    )
                                  }
                                  value={{ label: this.state.selectedContractNumber, value: this.state.selectedContractNumber, suspendStatus: this.state.isSelectedContractNumberSuspended }}
                                  onChange={(item) => this.handleChange(item)}
                                  id="multi-contract-account-dropdown"
                                  placeholder={this.props.t(SEAMLESS_ACCESS_CONSTANTS.CA_DROPDOWN_PLACEHOLDER, { ns: `${this.state.nameSpace}` })}
                                  menuPlacement={this.props.windowSize.width > 480 ? "bottom" : "top"}
                          />


                  <div style={{ paddingTop: "0.5rem" }}>
                    <Body id="userVerification-helper-text" size="small">
                      {this.props.t(SEAMLESS_ACCESS_CONSTANTS.CA_DROPDOWN_HELPERTEXT, {
                        ns: `${this.state.nameSpace}`,
                      })}{" "}
                      <CustomisedTextLink
                        id="show-me-link"
                        color="#747676"
                        onClick={this.props.helpFindCAN}
                        size="small"
                        type="standAlone"
                        tabIndex="0"
                        role="link"
                        ariaLabel="click to view an Invoice sample "
                      >
                        {this.props.t(SEAMLESS_ACCESS_CONSTANTS.CA_DROPDOWN_HELPERTEXT_LINK, {
                          ns: `${this.state.nameSpace}`,
                        })}
                      </CustomisedTextLink>
                    </Body>
                  </div>
                </Wrapper>
              </Wrapper>
            </div>

            <CPButton
              id="verify-contract-number-button"
              style={{ marginTop: "2.5rem" }}
              boxButton={true}
              tabIndex="0"
              ariaLabel="Submit Contract Account Number"
              disabled={this.state.selectedContractNumber === ""}
              type="submit"
              onClick={this.handleSubmit}
              buttonWidth={getButtonWidth(this.props.windowSize)}
            >
              {this.props.t(SEAMLESS_ACCESS_CONSTANTS.SELECT_CA_BUTTON, {
                ns: `${this.state.nameSpace}`,
              })}
            </CPButton>
            {/* </div> */}
          </div>
          {/* </Wrapper> */}
          {/* </div> */}
        </div>
    );
  }
}
const mapStateToProp = (state) => {
  return {
    windowSize: state.ACCOUNT.windowSize,
    erpBusinessPartner: state.ACCOUNT.erpBusinessPartner,
    accountFinancialStatus: state.ACCOUNT.accountFinancialStatus,
    userLanguage: state.ACCOUNT.userLanguage,
    accountProfilePermission: state.ACCOUNT.accountProfilePermission,
    viewInvoicesPermission: state.ACCOUNT.viewInvoicesPermission,
    managePaymentsPermission: state.ACCOUNT.managePaymentsPermission,
    viewFinancialCasesPermission: state.ACCOUNT.viewFinancialCasesPermission,
    viewallotherCasesPermission: state.ACCOUNT.viewallotherCasesPermission,
    manageyourcontractandservicesPermission: state.ACCOUNT.manageyourcontractandservicesPermission,
    userType: state.ACCOUNT.userType,
    contractAccountCountry: state.ACCOUNT.contractAccountCountry,
    regionId: state.ACCOUNT.regionId,
    paymentStatus: state.ACCOUNT.paymentStatus,
    UniversalAccountId: state.ACCOUNT.accountUserTokenDetails.universalAccountId,
    accountUserTokenDetails: state.ACCOUNT.accountUserTokenDetails,
  };
};

SeamlessAccess.propTypes = {
  windowSize: PropTypes.object,
  erpBusinessPartner: PropTypes.object,
  accountFinancialStatus: PropTypes.string,
  userLanguage: PropTypes.string,
  accountProfilePermission: PropTypes.string,
  viewInvoicesPermission: PropTypes.string,
  managePaymentsPermission: PropTypes.string,
  viewFinancialCasesPermission: PropTypes.string,
  viewallotherCasesPermission: PropTypes.string,
  manageyourcontractandservicesPermission: PropTypes.string,
  userType: PropTypes.string,
  contractAccountCountry: PropTypes.string,
  regionId: PropTypes.string,
  paymentStatus: PropTypes.object,
  UniversalAccountId: PropTypes.string,
  t: PropTypes.func,
  i18n: PropTypes.object,
  setContractAccount: PropTypes.func,
  getContractNumbersSuccess: PropTypes.func,
  landOnAuthorizedPage: PropTypes.func,
  helpFindCAN: PropTypes.func,
  accountUserTokenDetails: PropTypes.object,
};

export default withTranslation(["loginTranslation", "redBallTranslation"])(
  withRouter(
    connect(mapStateToProp, {
      getContractNumbersSuccess: Actions.getContractNumbersSuccess,
      setContractAccount: BillingActions.setContractAccount,
    })(SeamlessAccess)
  )
);
